import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageContext } from '@hero/web-context';
import { AddonsList } from '@hero/web-components';
import {
  useSubscriptionAddAddonApi,
  useSubscriptionMineApi,
  useSubscriptionRemoveAddonApi,
} from '@hero/api-hooks';
import { getObjectId } from '@hero/schema';
import { buildBillingReturnUrl } from '@hero/web-hooks';

/** Standalone addons-management screen — also reachable from Overview. */
export default function BillingAddonsPage() {
  const { t } = useTranslation('m-billing');
  const { setPageName } = usePageContext();
  const subscriptionQuery = useSubscriptionMineApi();
  const addAddon = useSubscriptionAddAddonApi();
  const removeAddon = useSubscriptionRemoveAddonApi();
  const subscription = subscriptionQuery.data ?? null;

  useEffect(() => {
    setPageName(t('addon.title'));
  }, [setPageName, t]);

  // Addons that produce a one-time billing redirect through Moyasar
  // hosted checkout — land on the shared payment-confirmation page
  // so the post-pay experience matches subscription checkouts.
  const addonCallbackUrl = buildBillingReturnUrl();

  const onAdd = useCallback(
    async (args: { code: string; billing: 'recurring' | 'one_time' }) => {
      if (!subscription) return;
      await addAddon.mutateAsync({
        _id: getObjectId(subscription._id),
        code: args.code,
        billing: args.billing,
        quantity: 1,
        ...(args.billing === 'one_time' ? { callbackUrl: addonCallbackUrl } : {}),
      });
    },
    [subscription, addAddon, addonCallbackUrl],
  );

  const onRemove = useCallback(
    async (args: { code: string }) => {
      if (!subscription) return;
      await removeAddon.mutateAsync({
        _id: getObjectId(subscription._id),
        code: args.code,
      });
    },
    [subscription, removeAddon],
  );

  return (
    <div className="mt-6">
      <AddonsList
        subscription={subscription}
        onAddAddon={onAdd}
        onRemoveAddon={onRemove}
        onPurchaseOneTime={code => onAdd({ ...code, billing: 'one_time' })}
      />
    </div>
  );
}
